import React, { useEffect, useState } from 'react';
import ReactModal from 'react-modal';
import { ReactComponent as CloseModalIcon } from '../../assets/svg/CloseModalIcon.svg';
import { ReactComponent as Save } from '../../assets/svg/Save.svg';
import { Controller, useForm } from 'react-hook-form';
import MDEditor from '@uiw/react-md-editor';
import Loading from '../Loading';
import { updateAiPrompt } from '../../server/server';
import { notify } from '../../utils/util';

interface EditPromptModalProps {
	modalIsOpen: boolean;
	closeModal: () => void;
	prompt: { key: string; content: string } | null;
	reloadPrompts: () => void;
}

function EditPromptModal({ modalIsOpen, closeModal, prompt, reloadPrompts }: EditPromptModalProps) {
	const [processing, setProcessing] = useState(false);

	const { handleSubmit, reset, setValue, control, formState } = useForm({
		defaultValues: {
			content: '',
		},
	});

	useEffect(() => {
		ReactModal.setAppElement('#root');
		const element = document.getElementById('home');
		if (modalIsOpen) {
			if (!!element) element.style.filter = 'blur(5px)';
			if (prompt) {
				setValue('content', prompt.content);
			}
		} else {
			if (!!element) element.style.filter = 'unset';
		}
	}, [modalIsOpen]);

	const onSubmit = async (data: { content: string }) => {
		if (!prompt) return;
		setProcessing(true);
		const resp = await updateAiPrompt(prompt.key, data.content);
		if (resp.status === 200) {
			notify('Prompt updated successfully!');
			setProcessing(false);
			closeModal();
			reloadPrompts();
		} else {
			notify('Failed to update prompt!');
			setProcessing(false);
		}
	};

	const cleanUpFunction = () => {
		reset();
		setProcessing(false);
	};

	return (
		<ReactModal
			isOpen={modalIsOpen}
			onAfterClose={() => cleanUpFunction()}
			onRequestClose={closeModal}
			style={{
				overlay: {
					zIndex: 0,
					background: '#00000045',
				},
				content: {
					padding: 0,
					paddingTop: 30,
					paddingBottom: 30,
					opacity: 1,
					width: 900,
					margin: 'auto',
					backgroundColor: 'transparent',
					maxHeight: '100vh',
					border: 'none',
					boxShadow: 'none',
					overflow: 'auto',
				},
			}}
			className={'scrollbar-display-none'}
		>
			<div className='student-info-modal'>
				<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
					<div className='close-modal-icon' onClick={() => closeModal()}>
						<CloseModalIcon />
					</div>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className='bg-white rounded-xl mx-3 flex-1 flex flex-col gap-5 px-[40px] py-[30px]'>
					<div className='flex flex-row items-center'>
						<div className='flex-1'>
							<p className='text-primaryViolet font-semibold text-3xl text-left'>Edit Prompt</p>
						</div>
					</div>

					<div>
						<p className='text-mudPurple font-medium text-sm mb-1'>Key (read-only)</p>
						<div className='flex h-[50px] flex-row items-center rounded-[10px] border border-primaryViolet bg-[#F8F8FB] px-4'>
							<p className='text-[16px] font-semibold text-primaryViolet'>{prompt?.key}</p>
						</div>
					</div>

					<div>
						<p className='text-primaryViolet font-semibold text-lg text-left mb-1'>Content</p>
						<Controller
							control={control}
							name='content'
							rules={{ required: true }}
							render={({ field }) => (
								<MDEditor
									data-color-mode='light'
									value={field.value}
									onChange={(value: any) => setValue('content', value ?? '', { shouldValidate: true })}
									preview='edit'
									height={300}
								/>
							)}
						/>
						{formState.errors.content?.type === 'required' && (
							<p className='text-redError font-medium text-sm mt-1'>* This field is required</p>
						)}
					</div>

					<div className='flex flex-row justify-end w-full gap-5 mt-6'>
						<div
							className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-[#fafaff] h-[60px] button-transparent-class'
							onClick={closeModal}
						>
							<p className='text-[18px] text-primaryViolet leading-5 font-semibold'>Cancel</p>
						</div>
						<button
							className='flex-1 max-w-[160px] flex flex-row items-center justify-center cursor-pointer rounded-full bg-primaryViolet min-h-[60px] button-save-class transition'
							disabled={processing}
						>
							{processing ? (
								<Loading primaryColor='#FDDF6D' secondaryColor='#5E639B' size={25} stroke={7} />
							) : (
								<div className='flex flex-row items-center justify-center'>
									<p className='text-[18px] leading-5 font-semibold mr-3'>Save</p>
									<Save />
								</div>
							)}
						</button>
					</div>
				</form>
			</div>
		</ReactModal>
	);
}

export default EditPromptModal;
